import {ChangeEvent, useCallback, useEffect, useRef, useState} from "react";
import PageableViewModel from "@contents/viewModels/pageableViewModel";
import MemberRepository from "@services/repositories/memberRepository";
import {useRecoilValue} from "recoil";
import {brandState, memberState, shopState, spaceState} from "@services/stores/storeState";
import {_OPERATION_TYPE} from "@app/constants/types";
import {
    MemberAccessPutPayload,
    MemberEntity,
    MemberInfoPayload,
    MemberInviteInfoEntity,
    MemberInviteInfoPayload,
    MemberInviteListPayload,
    MemberListGetPayload,
    MemberPasswordPutPayload,
    MemberPayload,
    MemberResetPasswordPutPayload
} from "@services/entities/memberEntity";
import {useNavigate} from "react-router-dom";
import {RouteUrl} from "@app/constants/routeUrl";
import {MemberSearchType} from "@app/constants/searchTypes";
import {ResponseEntity} from "@services/entities/baseEntity";
import {IdEntity} from "@services/entities/commonEntity";
import {
    getCertifyErrorToast,
    getCertifySuccessToast,
    getDeleteErrorAlert,
    getDeleteSuccessToast,
    getNoCheckErrorAlert,
    getSaveErrorAlert,
    getSaveSuccessToast,
    setSort
} from "@utils/commonUtils";
import {eAlert, eConfirm} from "@app/events";
import {Strings} from "@app/constants/strings";
import {ETableRefModel} from "@contents/models/commonModel";
import {cloneDeepWith} from "lodash";

const MemberViewModel = () => {
    const navigate = useNavigate();
    const memberRepository = MemberRepository();
    const pageable = PageableViewModel();
    const spaceInfo = useRecoilValue(spaceState);
    const brandInfo = useRecoilValue(brandState);
    const shopInfo = useRecoilValue(shopState);
    const memberInfo = useRecoilValue(memberState);

    const memberList = memberRepository.memberListData;
    const memberData = memberRepository.memberDetailData?.content;
    const memberInviteList = memberRepository.memberInviteListData?.content;
    const roleList = memberRepository.memberRoleListData?.content;
    const memberInviteInfo = memberRepository.memberInviteInfoData?.content;
    const memberShopInviteInfo = memberRepository.memberInviteShopInfoData?.content;

    const memberInviteState = memberRepository.memberInviteStatus;

    const tableRef = useRef<ETableRefModel>(null);

    const [memberEdit, setMemberEdit] = useState<MemberEntity>();
    const [memberPassword, setMemberPassword] = useState<MemberPasswordPutPayload>();
    const [searchTypeInput, setSearchTypeInput] = useState<MemberSearchType>('name');
    const [searchText, setSearchText] = useState<string>('');
    // 멤버 초대 정보 state
    const [memberSignupPayload, setMemberSignupPayload] = useState<MemberInfoPayload>();
    const [selectedCheckList, setSelectedCheckList] = useState<Array<number>>([]);

    const [signUpMemberInviteInfo, setSignUpMemberInviteInfo] = useState<MemberInviteInfoEntity>();
    const [memberSearch, setMemberSearch] = useState<MemberListGetPayload>({
        brandCode: spaceInfo.brandCode,
        page: pageable.page,
        size: pageable.rowPerPage,
        sort: [setSort('id', 'DESC')]
    });

    // 멤버 리스트 조회
    const getMemberSearch = useCallback(() => {
        console.log(memberSearch)
        const params: MemberListGetPayload = cloneDeepWith(memberSearch);

        params.shopId = spaceInfo.selectedSystem === 'BRAND' ? undefined : shopInfo.id;

        if (searchTypeInput && searchText && searchText != '') {
            searchTypeInput === 'name' && (params.name = searchText);
            searchTypeInput === 'userId' && (params.userId = searchText);
        }

        memberRepository.getMemberList(params);
    }, [searchTypeInput, searchText, memberSearch]);

    // 시스템에 따라 멤버 상세페이지 이동
    const goMemberDetail = (row: MemberEntity, callType: _OPERATION_TYPE) => {
        switch (callType) {
            case "BRAND":
                navigate(RouteUrl.brand.setting.memberEdit.url + '/' + row.id);
                break;
            case "CAFE":
                navigate(RouteUrl.cafe.setting.memberEdit.url + '/' + row.id);
                break;
            case "DINING":
                navigate(RouteUrl.dining.setting.memberEdit.url + '/' + row.id);
                break;
        }
    }

    // 멤버 상세 조회
    const getMemberDetail = (id: number) => {
        memberRepository.getMemberDetail({id});
    }

    // 멤보 상세 조회시 수정 State에 저장
    useEffect(() => {
        memberData && setMemberEdit(memberData);
    }, [memberData]);

    // 멤버 수정.
    const onMemberEdit = (attachId?: number, callback?: (result: boolean) => void) => {
        memberRepository.onMemberEdit({
            id: memberEdit?.id,
            name: memberEdit?.name,
            memo: memberEdit?.memo,
            fileAttachId: attachId ?? memberEdit?.fileAttachId,
            companyIds: memberEdit?.memberCompanyMaps?.map(company => company.companyId!),
        }, (result: ResponseEntity<boolean>) => {
            if (result.content) {

                callback ? callback(result.content) : getSaveSuccessToast()
            } else {
                getSaveErrorAlert()
            }
        })
    }

    // 내 계정 수정.
    const onMyEdit = (attachId?: number, callback?: (result: ResponseEntity<string>) => void) => {
        memberRepository.onMyEdit({
            id: memberInfo.id,
            fileAttachId: attachId,
        }, (result: ResponseEntity<string>) => {
            callback && callback(result);
        })
    }

    // 멤버 비밀번호 수정.
    const onMemberPasswordChange = (callback: (result: ResponseEntity<boolean>) => void) => {
        memberRepository.onMemberChangePassword({
            ...memberPassword,
            callback: (result: ResponseEntity<boolean>) => {
                result && callback(result);
            }
        });
    }

    // 멤버 프로필 이미지 수정.
    const onMemberImageChange = (attachId?: number) => {
        attachId && setMemberEdit({
            ...memberEdit,
            fileAttachId: attachId
        })
    }

    // 멤버 정보 중 텍스트 항목 수정.
    const onMemberTextChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setMemberEdit({
            ...memberEdit,
            [event.target.name]: event.target.value,
        })
    }

    // 초대한 멤버 리스트 조회
    const getMemberInviteList = (callType: _OPERATION_TYPE) => {
        const params: MemberInviteListPayload = {
            brandCode: spaceInfo.brandCode,
        }

        callType != 'BRAND' && (params.shopId = shopInfo.id);

        memberRepository.getMemberInviteList(params)
    }

    // 멤버 권한 리스트 조회
    const getRoleList = () => {
        memberRepository.getMemberRoleList();
    }

    // 멤버 초대
    const onMemberInvite = (email: string, roleId: number, callType: _OPERATION_TYPE, callback?: (data) => void) => {
        const params: MemberPayload = {
            brandCode: spaceInfo.brandCode,
            email,
            roleId
        }

        callType != 'BRAND' && (params.shopId = shopInfo.id);

        memberRepository.onMemberInvite(params, (data) => {
            // error의 종류에 따라 Alert인지 문구인지 결정.
            callback && callback(data);
        })
    }

    // 초대한 멤버 삭제
    const onMemberInviteDelete = (mailCode: string, callType: _OPERATION_TYPE) => {
        const params: MemberInviteInfoPayload = {
            brandCode: spaceInfo.brandCode,
            mailCode
        }

        callType != 'BRAND' && (params.shopId = shopInfo.id);

        memberRepository.onMemberInviteDelete(params)
    }

    // 기존 멤버 shop 추가.
    const onMemberAddShop = (email: string, roleId: number, callType: _OPERATION_TYPE, callback?: () => void) => {
        const params: MemberPayload = {
            brandCode: spaceInfo.brandCode,
            email,
            roleId,
            callback: (result: ResponseEntity<boolean>) => {
                if (result.content) {
                    callback && callback();
                } else {
                    eAlert(Strings.alert.serverError);
                }
            }
        }

        callType != 'BRAND' && (params.shopId = shopInfo.id);

        memberRepository.onMemberAddShop(params);
    }

    // 멤버 초대 이메일 정보 확인 호출.
    const getMemberInviteInfo = ({mailCode, brandCode, shopId}: MemberInviteInfoPayload) => {
        memberRepository.getMemberInviteInfo({
            mailCode, brandCode, shopId
        })
    }

    // 멤버 추가 샵 초대 이메일 정보 확인 호출.
    const getMemberShopInviteInfo = ({mailCode, brandCode, shopId}: MemberInviteInfoPayload) => {
        memberRepository.getMemberShopInviteInfo({
            mailCode, brandCode, shopId
        })
    }

    // 회원가입
    const onMemberSignUp = () => {
        memberSignupPayload && memberRepository.onMemberSignUp(memberSignupPayload, (data?: ResponseEntity<IdEntity>, params?: MemberInfoPayload, errorResponse?: any) => {
            if(data?.content?.id) {
                navigate(RouteUrl.auth.complete.url, {
                    state: {
                        email: memberSignupPayload.email,
                    }
                });
            }else{
                errorResponse?.details ? eAlert(`${Strings.alert.serverError}\n\n${errorResponse?.details}`) : eAlert(`${Strings.alert.serverError}`);
            }
        })
    }

    // 멤버 체크리스트
    const onSelectedList = (checkList: Array<number>) => {
        setSelectedCheckList(checkList);
    }

    // 멤버 삭제
    const onMemberDelete = (callType: _OPERATION_TYPE) => {
        const filterList = selectedCheckList.filter(id => id);
        if (filterList.length > 0) {
            const data = {
                ids: filterList,
                brandCode: spaceInfo.brandCode,
                shopId: callType === 'BRAND' ? undefined : shopInfo.id,
            };

            eConfirm(Strings.alert.memberDelete, () => memberRepository.onMemberDelete(data, (result: ResponseEntity<boolean>) => {
                if (result && result.content) {
                    getDeleteSuccessToast();
                } else {
                    getDeleteErrorAlert();
                }
            }));
        } else {
            getNoCheckErrorAlert();
        }
    }

    // 비밀번호 초기화
    const onMemberPasswordReset = (userId: string, userPwd: string, callback: (result: ResponseEntity<boolean>) => void) => {
        const param: MemberResetPasswordPutPayload = {
            brandCode: spaceInfo.brandCode,
            userId,
            userPwd,
            callback
        };
        memberRepository.onMemberResetPassword(param);
    };
    
    // 개인정보 접근 인증
    const onMemberCertifyAccess = (payload: MemberAccessPutPayload) => {
        const accessBrandCode = (spaceInfo.brandCode === '' ? spaceInfo.space : spaceInfo.brandCode) ?? '';
        const deviceInfo = spaceInfo.deviceInfo;

        const param: MemberAccessPutPayload = {
            ...payload,
            accessBrandCode,
            deviceInfo,
            callback: (result) => {
                if (result && result.content) {
                    getCertifySuccessToast();
                    payload.callback && payload.callback(); // 성공 시 모달 닫기
                } else {
                    getCertifyErrorToast();
                }
            }
        };
        memberRepository.onMemberCertifyAccess(param);
    };


    return {
        tableRef,
        pageable,
        memberList,
        memberData,
        memberInfo,
        getMemberSearch,
        getMemberDetail,
        goMemberDetail,
        onMemberInvite,
        onMemberEdit,
        memberEdit,
        setMemberEdit,
        onMemberTextChange,
        getMemberInviteList,
        getRoleList,
        roleList,
        memberInviteList,
        onMemberInviteDelete,
        memberPassword,
        setMemberPassword,
        onMemberPasswordChange,
        searchTypeInput,
        setSearchTypeInput,
        onMemberAddShop,
        getMemberInviteInfo,
        getMemberShopInviteInfo,
        memberSignupPayload, setMemberSignupPayload,
        memberInviteInfo,
        memberShopInviteInfo,
        onMemberSignUp,
        onMyEdit,
        onMemberImageChange,
        shopInfo,
        spaceInfo,
        brandInfo,
        onSelectedList,
        onMemberDelete,
        onMemberPasswordReset,
        onMemberCertifyAccess,
        memberInviteState,
        signUpMemberInviteInfo, setSignUpMemberInviteInfo,
        searchText, setSearchText,
        memberSearch, setMemberSearch,
        callMemberMeInMyMenu: memberRepository.callMemberMeInMyMenu
    }
}

export default MemberViewModel;